/**
 * ContextPanel — panneaux contextuels du cockpit vocal (Phase 1 A2, plan §2).
 *
 * Miroir « glanceable » du dialogue : n'affiche QUE ce qui est pertinent à la
 * phase courante (anti-surcharge, KISS). Petits composants purs pilotés par le
 * `ctx` exposé par `useDialogueSession` (A1) ; ZÉRO logique métier dupliquée —
 * la progression / les alertes / la relecture réutilisent `captureSpec`.
 *
 * Table phase → panneau (plan §2) :
 *   CONFIRM_PATIENT → carte patient
 *   SELECT_PATIENT  → liste candidats numérotés
 *   AWAIT_ACTION    → menu numéroté + badge DÛ
 *   CAPTURE guidé   → progression champ par champ ✓ / ➤ / – + alerte borne
 *   CAPTURE dictée  → transcript capturé
 *   AWAIT_COMMIT    → carte relecture déterministe (readback)
 */

import type { DialogueContext, DialogueStateName } from '../services/dialogueMachine';
import {
  captureModeFor,
  constanteAlert,
  guidedFields,
  readback,
} from '../services/captureSpec';

interface ContextPanelProps {
  phase: DialogueStateName;
  ctx: DialogueContext;
}

export function ContextPanel({ phase, ctx }: ContextPanelProps) {
  switch (phase) {
    case 'CONFIRM_PATIENT':
      return ctx.patient && ctx.chambre ? (
        <PatientCard chambre={ctx.chambre.numero} ctx={ctx} />
      ) : null;

    case 'SELECT_PATIENT':
      return ctx.chambre ? <CandidatesList ctx={ctx} /> : null;

    case 'AWAIT_ACTION':
      return ctx.subView === 'prescriptions'
        ? <PrescriptionsList ctx={ctx} />
        : <KeywordHub />;

    case 'CAPTURE':
      return ctx.action
        ? captureModeFor(ctx.action.section) === 'guided'
          ? <FieldProgress ctx={ctx} />
          : <Transcript ctx={ctx} />
        : null;

    case 'AWAIT_COMMIT':
      return ctx.action ? <ReadbackCard ctx={ctx} /> : null;

    default:
      return null;
  }
}

// ── CONFIRM_PATIENT : carte patient courant ─────────────────────────────────
function PatientCard({ chambre, ctx }: { chambre: string; ctx: DialogueContext }) {
  const p = ctx.patient!;
  return (
    <div className="vpanel vpanel-patient">
      <div className="vpanel-room">Chambre {chambre}</div>
      <div className="vpanel-name">{p.prenom} {p.nom}</div>
      <div className="vpanel-cue">Dites « validation »</div>
    </div>
  );
}

// ── SELECT_PATIENT : liste des candidats lettrés ────────────────────────────
function CandidatesList({ ctx }: { ctx: DialogueContext }) {
  return (
    <div className="vpanel">
      <div className="vpanel-title">Chambre {ctx.chambre!.numero} — quel patient ?</div>
      <ul className="vpanel-list">
        {ctx.chambre!.patients.map((p, i) => (
          <li key={p.patient_id}>
            <span className="vpanel-letter">{i + 1}</span>
            <span>{p.nom}</span>
          </li>
        ))}
      </ul>
      <div className="vpanel-cue">Dites le numéro du patient</div>
    </div>
  );
}

// ── AWAIT_ACTION (hub) : liste glanceable des mots-clés d'intention (AC2) ────
const HUB_KEYWORDS = ['prescriptions', 'constantes', 'transmission', 'score', 'refus', 'relire'];

function KeywordHub() {
  return (
    <div className="vpanel">
      <div className="vpanel-title">Que faire ?</div>
      <ul className="vpanel-list">
        {HUB_KEYWORDS.map((mot) => (
          <li key={mot}>
            <span>« {mot} »</span>
          </li>
        ))}
      </ul>
      <div className="vpanel-cue">Dites un mot-clé (ou un numéro)</div>
    </div>
  );
}

// ── subView prescriptions : sous-liste numérotée des administrations (AC3) ───
function PrescriptionsList({ ctx }: { ctx: DialogueContext }) {
  const admins = ctx.menu.filter((m) => m.section === 'administration');
  return (
    <div className="vpanel">
      <div className="vpanel-title">Prescriptions</div>
      <ul className="vpanel-list">
        {admins.map((m, i) => (
          <li key={m.lettre}>
            <span className="vpanel-letter">{i + 1}</span>
            <span>{m.medicament ?? m.label} {m.dose ?? ''}</span>
            {m.due && <span className="vpanel-due">● DÛ</span>}
          </li>
        ))}
      </ul>
      <div className="vpanel-cue">« administrer N » ou « administrer autre »</div>
    </div>
  );
}

// ── CAPTURE guidé : progression champ par champ + alerte borne ──────────────
function FieldProgress({ ctx }: { ctx: DialogueContext }) {
  const fields = guidedFields(ctx.action!.section);
  return (
    <div className="vpanel">
      <div className="vpanel-title">{ctx.action!.label}</div>
      <ul className="vpanel-fields">
        {fields.map((f, i) => {
          const value = ctx.fields[f.key];
          const done = value !== undefined && value.trim() !== '';
          const current = i === ctx.fieldIndex;
          const alert = done ? constanteAlert(f.key, value) : null;
          const mark = done ? '✓' : current ? '➤' : '–';
          return (
            <li key={f.key} className={done ? 'is-done' : current ? 'is-current' : 'is-todo'}>
              <span className="vpanel-mark">{mark}</span>
              <span className="vpanel-field-label">{f.prompt.replace(/\s*\?$/, '')}</span>
              {done && <span className="vpanel-field-value">{value}</span>}
              {alert && <span className="vpanel-alert" title={alert}>⚠</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── CAPTURE dictée : transcript capturé ─────────────────────────────────────
function Transcript({ ctx }: { ctx: DialogueContext }) {
  return (
    <div className="vpanel">
      <div className="vpanel-title">{ctx.action!.label}</div>
      <p className="vpanel-transcript">
        {ctx.captured ? ctx.captured : <span className="vpanel-cue">Je vous écoute…</span>}
      </p>
    </div>
  );
}

// ── AWAIT_COMMIT : carte relecture déterministe ─────────────────────────────
function ReadbackCard({ ctx }: { ctx: DialogueContext }) {
  const phrase = readback(ctx.action!.section, ctx.action!, ctx.fields, ctx.captured);
  return (
    <div className="vpanel vpanel-commit">
      <div className="vpanel-title">Relecture</div>
      <p className="vpanel-readback">{phrase}</p>
      <div className="vpanel-cue">« validation » · « corrige » · « annule »</div>
    </div>
  );
}
